/*
<summary>
   Operator Assign Task "Tool Release" button and popup.
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { XCircleFill } from 'react-bootstrap-icons';
import { useStore } from '../../../contexts/store-provider';
import ConfirmPopup from '../../../shared-components/popup/confirm/confirm-popup';
import { ToolBomUsageModeEnum } from '../../../models/response/IToolBOMResponse';
import {
  getTenantIdFromLocalStorage,
  getToolBOMFactoryIdFromLocalStorage,
} from '../../../helpers/localstorage-helper';

interface IProps {
  trayIds?: string[];
  processId?: number | string;
  mode?: ToolBomUsageModeEnum;
  hasTray?: boolean;
}

const ToolReleaseControl = ({ trayIds, processId, mode = ToolBomUsageModeEnum.Single, hasTray = true }: IProps) => {
  const { toolBOMStore } = useStore();
  const {
    scannedToolNumbers,
    ReleaseToolService,
    releaseToolState,
    selectedToolModel,
    GetToolBomToolManagementListService,
  } = toolBOMStore;

  const [showPopup, setShowPopup] = useState(false);
  const [pendingRelease, setPendingRelease] = useState<string[] | null>(null);

  // Tools can only be released once a tray is loaded
  useEffect(() => {
    if (!hasTray) {
      setShowPopup(false);
    }
  }, [hasTray]);

  const closePopup = () => {
    setShowPopup(false);
  };

  const confirmReleaseTool = (toolNo: string) => {
    setPendingRelease([toolNo]);
  };

  const confirmReleaseAll = () => {
    setPendingRelease(scannedToolNumbers);
  };

  const cancelPendingRelease = () => {
    setPendingRelease(null);
  };

  const submitPendingRelease = () => {
    const isReleaseAll = pendingRelease?.length === scannedToolNumbers.length;
    ReleaseToolService(
      pendingRelease || [],
      trayIds,
      processId !== undefined ? Number(processId) : undefined,
      mode
    ).then(() => {
      setPendingRelease(null);
      if (isReleaseAll) closePopup();

      if (releaseToolState.success && selectedToolModel) {
        GetToolBomToolManagementListService(
          selectedToolModel,
          getToolBOMFactoryIdFromLocalStorage() || '',
          Number(getTenantIdFromLocalStorage())
        );
      }
    });
  };

  return (
    <>
      <div className="card-title">
        <button
          type="button"
          className={`btn btn-outline-primary ${!hasTray ? 'disabled' : ''}`}
          disabled={!hasTray}
          onClick={() => setShowPopup(true)}
        >
          <FormattedMessage id="tool_release" />
        </button>
      </div>

      {showPopup && (
        <Modal
          show={showPopup}
          centered
          size="lg"
          className="tool-release-modal"
        >
          <Modal.Header>
            <Modal.Title>
              <FormattedMessage id="tool_release" />
              <button
                onClick={closePopup}
                className="Crossicons"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="23"
                  height="23"
                  fill="currentColor"
                  className="bi bi-x"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex justify-content-between px-3 mb-2">
              <span className="fw-bold">
                <FormattedMessage id="tool_management_no" />
              </span>
              <span className="fw-bold">
                <FormattedMessage id="release" />
              </span>
            </div>
            <ul className="list-group">
              {scannedToolNumbers.length > 0 ? (
                scannedToolNumbers.map((toolNo) => (
                  <li
                    key={toolNo}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    {toolNo}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-danger"
                      disabled={releaseToolState.inProgress}
                      onClick={() => confirmReleaseTool(toolNo)}
                    >
                      <XCircleFill size={20} />
                    </button>
                  </li>
                ))
              ) : (
                <li className="list-group-item text-muted text-center">
                  <FormattedMessage id="no_tools_to_release" />
                </li>
              )}
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closePopup}
            >
              <FormattedMessage id="cancel" />
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={scannedToolNumbers.length === 0 || releaseToolState.inProgress}
              onClick={confirmReleaseAll}
            >
              <FormattedMessage id="release_all" />
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {pendingRelease && (
        <ConfirmPopup
          title="confirmation"
          message={
            pendingRelease.length > 1
              ? 'confirm_release_tool_all'
              : 'confirm_release_tool'
          }
          messageValues={
            pendingRelease.length > 1
              ? { toolNos: pendingRelease.join(', ') }
              : { toolNo: pendingRelease[0] }
          }
          isLoading={releaseToolState.inProgress}
          modalClosed={cancelPendingRelease}
          modalSubmit={submitPendingRelease}
        />
      )}
    </>
  );
};

export default observer(ToolReleaseControl);
